import stringToNumber from "./stringToNumber";

const isValidDateForSort = (dateString: any) =>
  dateString?.split("/").length === 3 || dateString?.split("-").length === 3;

const sortCurrencyAndPercentageString = (a: string, b: string) => {
  const number = /[0-9,]+(\.[\d.]+)?/;

  // Currency: optional minus, $, then number
  const currencyRegex = new RegExp(String.raw`^-?\$\s*${number.source}$`);

  // Percentage: optional minus, number, then %
  const percentageRegex = new RegExp(String.raw`^-?${number.source}%$`);

  const isStringACurrencyOrPercentage =
    currencyRegex.test(a) || percentageRegex.test(a);
  const isStringBCurrencyOrPercentage =
    currencyRegex.test(b) || percentageRegex.test(b);

  if (!isStringACurrencyOrPercentage || !isStringBCurrencyOrPercentage) {
    return null;
  }

  return (Number(a?.replaceAll(/[$,%]/g, "")) - Number(b?.replaceAll(/[$,%]/g, "")));
};

const sortAlphaNumericString = (a: any, b: any) =>
  a?.localeCompare(b, undefined, {
    sensitivity: "base",
    numeric: true
  });

// Parse size values: extract number and unit
const parseSize = (str: string) => {
  // Handle various size formats: "0.5 LB", "1 CT", "4 IN", "0.3 OZ", "12.5", etc.
  const sizeRegex = /^([\d.]+)\s*([A-Z]+)$/i;
  const match = sizeRegex.exec(str);
  if (!match) {
    // Try to extract just a number if no unit found
    const numericRegex = /^([\d.]+)/;
    const numericMatch = numericRegex.exec(str);
    if (numericMatch) {
      return {
        numeric: Number.parseFloat(numericMatch[1]),
        unit: null,
        original: str
      };
    }
    return { numeric: null, unit: null, original: str };
  }

  return {
    numeric: Number.parseFloat(match[1]),
    unit: match[2].toUpperCase(),
    original: str
  };
};

// Helper function to compare sizes with units
const compareSizesWithUnits = (sizeA: any, sizeB: any): number => {
  if (sizeA.unit === sizeB.unit) {
    return sizeA.numeric - sizeB.numeric;
  }

  const unitComparison = sizeA.unit.localeCompare(sizeB.unit);
  return unitComparison === 0 ? sizeA.numeric - sizeB.numeric : unitComparison;
};

// Helper function to compare sizes with mixed unit scenarios
const compareSizesWithMixedUnits = (sizeA: any, sizeB: any): number => {
  if (sizeA.unit && !sizeB.unit) return -1;
  if (!sizeA.unit && sizeB.unit) return 1;
  if (!sizeA.unit && !sizeB.unit) {
    return sizeA.numeric - sizeB.numeric;
  }
  return 0;
};

// Helper function to compare sizes when only one is valid
const compareSizesWithOneValid = (sizeA: any, sizeB: any): number => {
  if (sizeA.numeric !== null && sizeB.numeric === null) return -1;
  if (sizeB.numeric !== null && sizeA.numeric === null) return 1;
  return 0;
};

const sortSizeValues = (
  a: string | null | undefined,
  b: string | null | undefined
): number => {
  // Handle null/undefined cases
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;

  const sizeA = parseSize(a);
  const sizeB = parseSize(b);

  // Both have numeric values and units
  if (
    sizeA.numeric !== null &&
    sizeB.numeric !== null &&
    sizeA.unit &&
    sizeB.unit
  ) {
    return compareSizesWithUnits(sizeA, sizeB);
  }

  // Both have numeric values but different unit scenarios
  if (sizeA.numeric !== null && sizeB.numeric !== null) {
    const mixedUnitResult = compareSizesWithMixedUnits(sizeA, sizeB);
    if (mixedUnitResult !== 0) return mixedUnitResult;
  }

  // Only one has valid numeric value
  const oneValidResult = compareSizesWithOneValid(sizeA, sizeB);
  if (oneValidResult !== 0) return oneValidResult;

  // Fall back to string comparison
  return a.localeCompare(b, undefined, {
    sensitivity: "base",
    numeric: true
  });
};

const sortDate = (a: any, b: any) => {
  // check if its in date format
  if (!isValidDateForSort(a) || !isValidDateForSort(b)) {
    return null;
  }

  return new Date(a).valueOf() - new Date(b).valueOf();
};

const sortNumericValues = (a: string, b: string) => {
  const numberRegex = /^-?[\d,]+(\.\d+)?$/;

  if (numberRegex.test(a) && numberRegex.test(b)) {
    return stringToNumber(a) - stringToNumber(b);
  }

  return null;
};

const sorter = (data: any, sortParam: any, sortOrder: any) =>
  data.sort((objA: any, objB: any) => {
    const dataA = objA[sortParam]?.toString();
    const dataB = objB[sortParam]?.toString();

    if (sortOrder === "ASC") {
      return (
        sortCurrencyAndPercentageString(dataA, dataB) ||
        sortNumericValues(dataA, dataB) ||
        sortDate(dataA, dataB) ||
        sortSizeValues(dataA, dataB) ||
        sortAlphaNumericString(dataA, dataB)
      );
    }

    return (
      sortCurrencyAndPercentageString(dataB, dataA) ||
      sortNumericValues(dataB, dataA) ||
      sortDate(dataB, dataA) ||
      sortSizeValues(dataB, dataA) ||
      sortAlphaNumericString(dataB, dataA)
    );
  });

export { sorter };
